import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { fetchMyWorkspaces, createWorkspace, Workspace } from '@/api/workspaces.api';

/**
 * Workspace Bootstrap Hook
 * 
 * Handles the workspace bootstrap flow after login:
 * 1. Fetches user's workspaces
 * 2. If empty, creates a trial workspace
 * 3. Returns the workspace list and loading state
 */
export function useWorkspaceBootstrap() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch user's workspaces
  const {
    data: workspaces = [],
    isLoading: isLoadingWorkspaces,
    error: workspacesError,
    refetch: refetchWorkspaces,
  } = useQuery<Workspace[]>({
    queryKey: ['workspaces', 'my'],
    queryFn: fetchMyWorkspaces,
    enabled: !!user, // Only fetch when user is authenticated
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Create trial workspace mutation
  const createTrialWorkspace = useMutation({
    mutationFn: async () => {
      // Generate a default workspace name based on user email or use a generic name
      const defaultName = user?.email 
        ? `${user.email.split('@')[0]}'s Workspace`
        : 'My Workspace';
      
      return createWorkspace({
        name: defaultName,
        plan_code: 'trial',
      });
    },
    onSuccess: (data) => {
      // Invalidate and refetch workspaces list
      queryClient.invalidateQueries({ queryKey: ['workspaces', 'my'] });
      
      // Navigate to the newly created workspace
      if (data.workspace?.slug) {
        navigate(`/${data.workspace.slug}`, { replace: true });
      }
    },
    onError: (error) => {
      console.error('Failed to create trial workspace:', error);
    },
  });

  // Auto-create trial workspace if no workspaces exist
  const shouldCreateTrial = !isLoadingWorkspaces && workspaces.length === 0 && !!user && !createTrialWorkspace.isPending && !createTrialWorkspace.isSuccess;

  // Bootstrap logic: create trial workspace if needed
  const bootstrap = async () => {
    if (shouldCreateTrial && !createTrialWorkspace.isPending && !createTrialWorkspace.isSuccess) {
      try {
        await createTrialWorkspace.mutateAsync();
      } catch (error) {
        console.error('Bootstrap failed to create workspace:', error);
        // Don't throw - let the app continue
      }
    }
  };

  return {
    workspaces,
    isLoading: isLoadingWorkspaces || createTrialWorkspace.isPending,
    error: workspacesError || createTrialWorkspace.error,
    shouldCreateTrial,
    createTrialWorkspace: createTrialWorkspace.mutateAsync,
    bootstrap,
    refetchWorkspaces,
  };
}

